import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { JsonConfigEditor, type ConfigValue } from "@/components/JsonConfigEditor";
import { SchemaForm } from "@/components/SchemaForm";
import { Toggle } from "@/components/Toggle";
import { hasProperties } from "@/lib/schema-form";
import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { fieldScope } from "@/lib/binding-scope";
import { hasWriteOn } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type {
  BindingScope,
  IntegrationCategory,
  ProvenanceSource,
  ProviderBinding,
  ProviderCatalogEntry,
  ProviderCredential,
  ProviderDevice,
  ResolvedBinding,
  ResolvedBindingsResponse,
} from "@tilt/admin-sdk";

// Push 17 phase 3 - the Providers tab splits by integration family.
// Payment is the default because it's the most common today; adding
// DMS / inventory / communication / analytics tabs lets admins manage
// those without changing pages. Inventory ordered next to DMS - the
// two are the dealer-side data integrations.
const CATEGORIES: Array<{ slug: IntegrationCategory; label: string }> = [
  { slug: "payment", label: "Payment" },
  { slug: "dms", label: "DMS" },
  { slug: "inventory", label: "Inventory" },
  { slug: "communication", label: "Communication" },
  { slug: "analytics", label: "Analytics" },
  { slug: "ai", label: "AI" },
];

/**
 * Reusable Providers tab for Partner / Organization / Location
 * detail pages. Shows the resolved cascade (what will actually fire
 * at this scope, walking up the tree) with clear inheritance badges
 * and one-click overrides.
 *
 * Designed for fast onboarding:
 *   - "Add provider" opens a modal that pre-selects sensible
 *     defaults (the first enabled provider × payment-type combo).
 *   - Inherited rows render read-only with an "Override here" button
 *     that spawns the same modal prefilled with the inherited row's
 *     config - so a partner-wide Aeropay key can be overridden at
 *     one location without re-typing anything.
 *   - Device management surfaces only when the provider supports
 *     devices AND the scope is a location (devices are always
 *     location-scoped).
 */

export type ProvidersTabScope = (
  | { kind: "partner"; id: string; name: string }
  | { kind: "organization"; id: string; name: string }
  | { kind: "location"; id: string; name: string }
) & {
  /** Partner ltree path covering this scope - ``platform.finterra`` for
   *  the Finterra partner row, the partner path for an org/location.
   *  Used by the write-gate predicate so Add / Edit / Delete /
   *  Sync-devices only surface when the caller has write scope on
   *  this subtree. Always passed from the detail-page caller. */
  partnerPath: string;
};

export function ProvidersTab(props: { scope: ProvidersTabScope }): JSX.Element {
  const { scope } = props;
  const qc = useQueryClient();
  const profile = useRoleProfile();
  // All binding mutations (create / patch / delete / apply /
  // sync-devices) share require_write(partner_path) on the server,
  // so a single gate covers every action rendered by this tab.
  const canWrite = hasWriteOn(profile, scope.partnerPath);
  const scopeParams =
    scope.kind === "partner"
      ? { partner_id: scope.id }
      : scope.kind === "organization"
        ? { organization_id: scope.id }
        : { location_id: scope.id };

  const resolved = useQuery({
    queryKey: ["provider-bindings-resolved", scope.kind, scope.id],
    queryFn: () => apiClient.getResolvedBindings(scopeParams),
    retry: false,
  });

  const catalog = useQuery({
    queryKey: ["provider-catalog"],
    queryFn: () => apiClient.listProviderCatalog(),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<{
    mode: "create" | "override" | "edit";
    binding?: ProviderBinding;
    // The credential currently resolved for this binding via ancestry -
    // passed through so the dialog's enum resolver (Valor merchants /
    // devices / etc.) can fetch even when the row's own credential_id
    // is NULL ("Inherit from ancestor"). Without this fallback the
    // config dropdowns stick on "Loading…" whenever the binding is
    // inheriting a credential from an ancestor.
    resolvedCredentialId?: string | null;
    // Push 13: full resolved entry so the dialog can render inherited
    // fields read-only with provenance pills.
    resolved?: ResolvedBinding;
    // Push 17: the category tab the Add button was clicked from. The
    // dialog filters its provider dropdown to this category so Add on
    // the DMS tab doesn't offer Valor.
    categoryHint?: IntegrationCategory;
  } | null>(null);

  // Push 17 phase 3 - activeCategory stays above the early returns
  // so React-hooks rules stay happy (the loading/error returns would
  // otherwise skip this useState between renders).
  const [activeCategory, setActiveCategory] = useState<IntegrationCategory>("payment");

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["provider-bindings-resolved"] });
    void qc.invalidateQueries({ queryKey: ["provider-bindings"] });
  };

  if (resolved.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading providers…</p>;
  }
  if (resolved.isError) {
    const msg =
      resolved.error instanceof Error ? resolved.error.message : "Failed to load providers";
    return <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
  }

  const data = resolved.data as ResolvedBindingsResponse;
  const providers: ProviderCatalogEntry[] = catalog.data?.providers ?? [];

  // Split bindings + "Add provider" by category. A binding's category
  // is inferred from its provider's catalog row (defaults to "payment"
  // if the catalog hasn't loaded yet OR the provider is absent from
  // the catalog). Only show tabs for categories that HAVE catalog
  // entries - no point offering DMS to admins when there's zero DMS
  // provider in the catalog.
  const availableCategories = CATEGORIES.filter((c) =>
    providers.some((p) => (p.category ?? "payment") === c.slug),
  );
  // Always keep Payment visible even when the catalog is empty mid-
  // load, so the default state doesn't flash an empty tab bar.
  const displayCategories =
    availableCategories.length > 0 ? availableCategories : CATEGORIES.slice(0, 1);
  const effectiveCategory: IntegrationCategory = displayCategories.some(
    (c) => c.slug === activeCategory,
  )
    ? activeCategory
    : (displayCategories[0]?.slug ?? "payment");

  const providersByCategory = (cat: IntegrationCategory): ProviderCatalogEntry[] =>
    providers.filter((p) => (p.category ?? "payment") === cat);
  const providerForBinding = (slug: string): ProviderCatalogEntry | undefined =>
    providers.find((p) => p.slug === slug);
  const categoryForBinding = (r: ResolvedBinding): IntegrationCategory =>
    providerForBinding(r.effective.provider_slug)?.category ?? "payment";

  const visibleResolved = data.resolved.filter((r) => categoryForBinding(r) === effectiveCategory);

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Effective providers</h2>
          <p className="text-xs text-muted-foreground">
            What will actually fire at this {scope.kind}. Rows inherited from an ancestor scope show
            where they came from - click "Override here" to attach a {scope.kind}-specific version.
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() =>
              setEditing({
                mode: "create",
                // Thread the current category into the create dialog so
                // it only offers providers from this tab.
                categoryHint: effectiveCategory,
              })
            }
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />{" "}
            {effectiveCategory === "payment"
              ? "Add provider"
              : `Add ${displayCategories.find((c) => c.slug === effectiveCategory)?.label ?? "Integration"} integration`}
          </button>
        )}
      </div>

      {displayCategories.length > 1 && (
        // Filter-button group, not a full ARIA tabs pattern (no
        // tabpanel / aria-controls / roving tabIndex). Keeping this
        // as plain buttons avoids half-implementing the ARIA tabs
        // spec and confusing screen readers; upgrade to a real
        // tablist if we add keyboard-navigable tabs.
        <div className="-mb-px flex gap-1 border-b">
          {displayCategories.map((c) => {
            const active = c.slug === effectiveCategory;
            const count = data.resolved.filter((r) => categoryForBinding(r) === c.slug).length;
            return (
              <button
                type="button"
                key={c.slug}
                aria-pressed={active}
                onClick={() => setActiveCategory(c.slug)}
                className={cn(
                  "relative -mb-px border-b-2 px-3 py-1.5 text-xs font-medium",
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {c.label}
                <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {visibleResolved.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/40 p-5 text-center text-sm text-muted-foreground">
          No{" "}
          {effectiveCategory === "payment"
            ? "providers"
            : `${displayCategories.find((c) => c.slug === effectiveCategory)?.label ?? effectiveCategory} integrations`}{" "}
          configured yet at this {scope.kind} (or any ancestor).
          {providersByCategory(effectiveCategory).some((p) => p.is_enabled)
            ? " Click Add to start."
            : ` No ${displayCategories.find((c) => c.slug === effectiveCategory)?.label ?? effectiveCategory} providers exist in the catalog yet.`}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleResolved.map((r) => (
            <BindingRow
              key={`${r.effective.provider_slug}-${r.effective.feature_path}`}
              resolved={r}
              currentScope={scope}
              providers={providers}
              canWrite={canWrite}
              onEdit={(b) =>
                setEditing({
                  mode: "edit",
                  binding: b,
                  resolvedCredentialId: r.credential_source?.credential_id ?? null,
                  resolved: r,
                })
              }
              onOverride={(b) =>
                setEditing({
                  mode: "override",
                  binding: b,
                  resolvedCredentialId: r.credential_source?.credential_id ?? null,
                  resolved: r,
                })
              }
              onMutated={invalidate}
            />
          ))}
        </div>
      )}

      {editing && (
        <BindingDialog
          mode={editing.mode}
          binding={editing.binding}
          resolvedCredentialId={editing.resolvedCredentialId ?? null}
          resolved={editing.resolved}
          scope={scope}
          // Push 17: restrict the provider dropdown in Create mode to
          // the category the admin was on when they clicked Add. In
          // edit/override modes the provider is fixed anyway, so this
          // only changes the Create experience.
          providers={
            editing.mode === "create" && editing.categoryHint
              ? providersByCategory(editing.categoryHint)
              : providers
          }
          // Existing bindings at THIS exact scope - used in create mode
          // to detect (provider, feature_path) collisions before the user
          // hits the V0060 unique-index 409 from the API.
          existingOwnScopeBindings={data.resolved.filter((r) => r.inherited_from === scope.kind)}
          onClose={() => setEditing(null)}
          onSwitchToEdit={(b, r) =>
            setEditing({
              mode: "edit",
              binding: b,
              resolvedCredentialId: r.credential_source?.credential_id ?? null,
              resolved: r,
            })
          }
          onMutated={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// One effective-binding row
// ---------------------------------------------------------------- //

function BindingRow(props: {
  resolved: ResolvedBinding;
  currentScope: ProvidersTabScope;
  providers: ProviderCatalogEntry[];
  onEdit: (b: ProviderBinding) => void;
  onOverride: (b: ProviderBinding) => void;
  onMutated: () => void;
  canWrite: boolean;
}): JSX.Element {
  const { resolved, currentScope, providers, canWrite } = props;
  const b = resolved.effective;
  const isOwnScope = resolved.inherited_from === currentScope.kind;
  const provider = providers.find((p) => p.slug === b.provider_slug);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn("rounded-md border p-3", b.is_enabled ? "bg-background" : "bg-muted/30")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{provider?.display_name ?? b.provider_slug}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
              {b.feature_path}
            </span>
            {!b.is_enabled && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                disabled
              </span>
            )}
            {!isOwnScope && (
              <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                <ArrowUp className="h-3 w-3" />
                inherited from {resolved.inherited_from}
              </span>
            )}
            {(() => {
              // Push 13b: only show "Action needed" when at least one
              // missing required-at-leaf field is at a scope the
              // current view can actually *edit*. SchemaForm only
              // allows editing fields whose x-scope === current scope
              // (less-specific fields are inherited + read-only), so
              // fixability is an exact match - not <= - the match.
              //
              // Fallback: if the catalog schema isn't loaded yet OR
              // any missing field isn't in the schema we can find,
              // surface the pill with the full missing_required list.
              // Suppressing it silently on an unknown schema would
              // hide genuine incomplete bindings during the initial
              // catalog fetch.
              if (resolved.status !== "incomplete") return null;
              const missing = resolved.missing_required ?? [];
              if (missing.length === 0) return null;

              const schemaProps = provider?.config_schema.properties;
              const missingWithKnownScope = schemaProps
                ? missing.filter((f) => schemaProps[f] !== undefined)
                : [];
              const scopeUnknown = !schemaProps || missingWithKnownScope.length < missing.length;

              let titleFields: string[];
              if (scopeUnknown) {
                titleFields = missing;
              } else {
                const fixableHere = missingWithKnownScope.filter(
                  (f) => fieldScope(schemaProps![f]) === currentScope.kind,
                );
                if (fixableHere.length === 0) return null;
                titleFields = fixableHere;
              }
              return (
                <span
                  className="rounded-full border border-amber-500/50 bg-amber-100/60 px-2 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
                  title={`Missing: ${titleFields.join(", ")}`}
                >
                  Action needed
                </span>
              );
            })()}
          </div>
          <CredentialBadge resolved={resolved} currentScope={currentScope} provider={provider} />
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
          {canWrite &&
            (isOwnScope ? (
              <>
                <button
                  type="button"
                  onClick={() => props.onEdit(b)}
                  className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
                >
                  <Settings className="h-3 w-3" /> Edit
                </button>
                <DeleteBindingButton binding={b} onMutated={props.onMutated} />
              </>
            ) : (
              <button
                type="button"
                onClick={() => props.onOverride(b)}
                className="rounded-md border bg-background px-2 py-1 text-xs text-primary hover:bg-muted"
              >
                Override at this {currentScope.kind}
              </button>
            ))}
        </div>
      </div>
      {expanded && (
        <BindingExpansion
          binding={b}
          provider={provider}
          currentScope={currentScope}
          isOwnScope={isOwnScope}
          canWrite={canWrite}
        />
      )}
    </div>
  );
}

function CredentialBadge(props: {
  resolved: ResolvedBinding;
  currentScope: ProvidersTabScope;
  provider: ProviderCatalogEntry | undefined;
}): JSX.Element {
  const src = props.resolved.credential_source;
  // "Journal-entry" providers (Cash, Check, and any future self-
  // contained payment types like manual store credit) have no API to
  // authenticate against, so they declare an empty credential_schema
  // in the platform catalog. Showing "No credential resolved -
  // payments will fail" for those is misleading: they don't need one.
  // Treat an empty credential_schema as a firm "this provider is
  // credential-less" signal.
  const needsCredential = hasProperties(props.provider?.credential_schema);
  if (!needsCredential) {
    return (
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        No credential required - recorded as a journal entry.
      </p>
    );
  }
  if (!src) {
    return (
      <p className="mt-0.5 text-[11px] text-destructive">
        No credential resolved - payments will fail. Attach a credential to this binding or an
        ancestor.
      </p>
    );
  }
  // Push 13c: surface both where the credential is STORED and where
  // the pin LIVES (if any). These are two distinct concepts that the
  // earlier single-line copy conflated.
  //
  //   storage scope - the credential row's own partner/org/location.
  //                   From credential_scope_kind / credential_scope_name.
  //   pin scope     - the binding row carrying credential_id.
  //                   From source_scope. null when auto_resolved.
  const credLabel = src.credential_label ?? "(unlabelled credential)";
  const storageScope = src.credential_scope_kind ?? src.source_scope ?? "ancestor";
  const storageName = src.credential_scope_name;
  const storageBit = storageName ? `${storageScope} (${storageName})` : String(storageScope);
  const autoResolved = src.auto_resolved === true;
  const pinScope = src.source_scope;

  let pinPhrase: string;
  if (autoResolved || !pinScope) {
    pinPhrase = `auto-resolved from ${storageBit}`;
  } else if (pinScope === storageScope) {
    pinPhrase = `pinned at ${storageBit}`;
  } else {
    pinPhrase = `pinned at ${pinScope}; stored at ${storageBit}`;
  }

  return (
    <p className="mt-0.5 text-[11px] text-muted-foreground">
      Using {credLabel} - {pinPhrase}.
    </p>
  );
}

function BindingExpansion(props: {
  binding: ProviderBinding;
  provider: ProviderCatalogEntry | undefined;
  currentScope: ProvidersTabScope;
  isOwnScope: boolean;
  canWrite: boolean;
}): JSX.Element {
  const { binding, provider, currentScope, isOwnScope, canWrite } = props;
  // Render every schema-declared field - not just the ones with a
  // set value. Admins complained that fields like
  // batch_settlement_time disappeared from the Config section when
  // nobody had populated them yet, leaving them to wonder whether
  // the field existed at all. Missing values now render as
  // "(not set)" so the full shape is always visible.
  const config = (binding.config ?? {}) as Record<string, unknown>;
  const schemaProps = (provider?.config_schema?.properties ?? {}) as Record<
    string,
    { title?: string }
  >;
  const schemaKeys = Object.keys(schemaProps);
  const extraKeys = Object.keys(config).filter((k) => !schemaProps[k]);
  const allKeys = [...schemaKeys, ...extraKeys];
  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Config
        </div>
        {allKeys.length === 0 ? (
          <p className="text-xs text-muted-foreground">This provider has no configurable fields.</p>
        ) : (
          <ul className="grid gap-1 text-xs sm:grid-cols-2">
            {allKeys.map((k) => {
              // Object.hasOwn avoids prototype-chain false positives
              // for schema keys like "toString"/"constructor" that
              // would otherwise be reported as set.
              const hasValue = Object.hasOwn(config, k);
              const label = schemaProps[k]?.title ?? k;
              const showBothLabelAndKey = label !== k;
              return (
                <li
                  key={k}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs",
                    hasValue ? "bg-muted/30" : "bg-muted/10 text-muted-foreground",
                  )}
                  title={showBothLabelAndKey ? k : undefined}
                >
                  <span className="font-medium">{label}</span>
                  {showBothLabelAndKey && (
                    <span className="ml-1 font-mono text-[10px] text-muted-foreground">({k})</span>
                  )}
                  :{" "}
                  {hasValue ? (
                    <span className="font-mono text-muted-foreground">
                      {JSON.stringify(config[k])}
                    </span>
                  ) : (
                    <span className="italic text-muted-foreground">(not set)</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {provider?.supports_devices && currentScope.kind === "location" && isOwnScope && (
        <DevicesSection binding={binding} canWrite={canWrite} />
      )}
      {provider?.supports_devices && currentScope.kind === "location" && !isOwnScope && (
        <p className="text-[11px] text-muted-foreground">
          This provider supports devices. Override the binding at this location to manage them.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Devices section
// ---------------------------------------------------------------- //

function DevicesSection(props: { binding: ProviderBinding; canWrite: boolean }): JSX.Element {
  const { binding, canWrite } = props;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["provider-devices", binding.id],
    queryFn: () => apiClient.listProviderDevices(binding.id),
  });
  const [addOpen, setAddOpen] = useState(false);
  const [deviceRef, setDeviceRef] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [metadata, setMetadata] = useState<ConfigValue>({});
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      apiClient.createProviderDevice(binding.id, {
        device_ref: deviceRef.trim(),
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
        metadata,
      }),
    onSuccess: () => {
      setDeviceRef("");
      setDisplayName("");
      setMetadata({});
      setAddOpen(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["provider-devices", binding.id] });
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const del = useMutation({
    mutationFn: (id: string) => apiClient.deleteProviderDevice(binding.id, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["provider-devices", binding.id] }),
  });

  const sync = useMutation({
    mutationFn: () => apiClient.syncProviderBindingDevices(binding.id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["provider-devices", binding.id] });
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const allDevices: ProviderDevice[] = q.data?.devices ?? [];

  // Filter devices by type based on feature_path.
  // "Soft Pos" or "virtual" in device_type = virtual terminal.
  // Everything else (e.g. "Desktop POS") = physical card-present terminal.
  const isVirtualDevice = (d: ProviderDevice) => {
    const t = String(d.metadata?.device_type ?? "").toLowerCase();
    return t.includes("soft") || t.includes("virtual");
  };
  const fp = binding.feature_path;
  const isVirtualBinding = fp === "pos.payments.virtual" || fp === "pos.payments.pay_by_text";
  const isCardPresentBinding = fp === "pos.payments.card_present";
  const devices = allDevices.filter((d) => {
    if (isCardPresentBinding) return !isVirtualDevice(d);
    if (isVirtualBinding) return isVirtualDevice(d);
    return true;
  });

  const cfg = (binding.config ?? {}) as Record<string, unknown>;
  // Only check the config key that's relevant for this binding type.
  const relevantConfigKey = isCardPresentBinding
    ? "card_present_devices"
    : isVirtualBinding
      ? "virtual_terminal_ids"
      : null;
  const configSerials: string[] = [];
  if (relevantConfigKey) {
    const raw = cfg[relevantConfigKey];
    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (typeof item === "string" && item) configSerials.push(item);
      }
    }
  }
  const syncedSerials = new Set(allDevices.map((d) => d.device_ref));
  const unsynced = configSerials.filter((s) => !syncedSerials.has(s));
  const hasUnsynced = unsynced.length > 0;

  const deviceTypeLabel = (d: ProviderDevice) => {
    const t = String(d.metadata?.device_type ?? "").trim();
    return t || null;
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Devices
        </div>
        {canWrite && (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              title="Pull device metadata from Valor and populate provider_device_tbl"
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3 w-3", sync.isPending && "animate-spin")} />
              {sync.isPending ? "Syncing…" : "Sync from Valor"}
            </button>
            <button
              type="button"
              onClick={() => setAddOpen((x) => !x)}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted"
            >
              <Plus className="h-3 w-3" /> Add manually
            </button>
          </div>
        )}
      </div>
      {hasUnsynced && (
        <div className="mb-2 rounded-md border border-amber-500/50 bg-amber-100/60 p-2 text-[11px] text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          <p className="font-medium">
            {unsynced.length} device{unsynced.length === 1 ? "" : "s"} selected in config but not
            yet synced from Valor
          </p>
          <p className="mt-0.5 text-[10px] opacity-80">
            Serials stored: {unsynced.map((s) => `"${s}"`).join(", ")}. Click "Sync from Valor" to
            pull full device metadata (name, type, last-synced time) into the registry.
          </p>
        </div>
      )}
      {q.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {devices.length === 0 && !q.isLoading && !hasUnsynced && (
        <p className="text-xs text-muted-foreground">No devices registered.</p>
      )}
      <ul className="space-y-1 text-xs">
        {devices.map((d) => {
          const typeLabel = deviceTypeLabel(d);
          return (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-md bg-background px-2 py-1"
            >
              <span className="flex items-center gap-1.5">
                <span className="font-medium">{d.display_name ?? d.device_ref}</span>
                <span className="font-mono text-muted-foreground">{d.device_ref}</span>
                {typeLabel && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {typeLabel}
                  </span>
                )}
                {!d.is_active && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    inactive
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => del.mutate(d.id)}
                aria-label={`Remove ${d.display_name ?? d.device_ref}`}
                className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          );
        })}
      </ul>
      {addOpen && (
        <div className="mt-2 rounded-md border bg-muted/30 p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              type="text"
              value={deviceRef}
              onChange={(e) => setDeviceRef(e.target.value)}
              placeholder="Device ID (vendor-side)"
              className="rounded-md border bg-background px-2 py-1 text-xs font-mono"
            />
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name (optional)"
              className="rounded-md border bg-background px-2 py-1 text-xs"
            />
          </div>
          <div className="mt-2">
            <JsonConfigEditor
              label="Metadata"
              hint="Per-device vendor-specific fields (app_id, app_key, serial number …)."
              value={metadata}
              onChange={setMetadata}
            />
          </div>
          {error && (
            <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
              {error}
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => add.mutate()}
              disabled={!deviceRef.trim() || add.isPending}
              className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Add device
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteBindingButton(props: {
  binding: ProviderBinding;
  onMutated: () => void;
}): JSX.Element {
  const m = useMutation({
    mutationFn: () => apiClient.deleteProviderBinding(props.binding.id),
    onSuccess: props.onMutated,
  });
  return (
    <button
      type="button"
      onClick={() => {
        if (confirm("Delete this provider binding?")) m.mutate();
      }}
      className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
    >
      <Trash2 className="h-3 w-3" />
    </button>
  );
}

// ---------------------------------------------------------------- //
// Binding create / edit / override dialog
// ---------------------------------------------------------------- //

function BindingDialog(props: {
  mode: "create" | "override" | "edit";
  binding?: ProviderBinding | undefined;
  // Ancestor-resolved credential for this binding (from the effective-
  // providers query). Used as the enum resolver's fallback when the
  // user leaves the Credential picker at "Inherit from ancestor" so
  // provider dropdowns (Valor merchants / devices / …) still load.
  resolvedCredentialId?: string | null;
  // Push 13: full resolved entry for the binding being edited - used to
  // populate inherited-field values in the SchemaForm (read-only pills
  // for fields that live at an ancestor scope).
  resolved?: ResolvedBinding | undefined;
  scope: ProvidersTabScope;
  providers: ProviderCatalogEntry[];
  // Bindings already owned at THIS scope. Drives the duplicate-detection
  // banner in create mode so the operator doesn't get a 409 from the
  // V0060 unique index. Empty in edit/override modes - those keep the
  // saved (provider, feature_path) tuple by definition.
  existingOwnScopeBindings?: ResolvedBinding[];
  onClose: () => void;
  // Switch the parent into edit mode for an existing binding - used by
  // the duplicate-detection banner's "Edit existing binding" button.
  onSwitchToEdit?: (b: ProviderBinding, r: ResolvedBinding) => void;
  onMutated: () => void;
}): JSX.Element {
  const { mode, binding, scope, providers } = props;
  const existingOwnScopeBindings = props.existingOwnScopeBindings ?? [];

  const [providerSlug, setProviderSlug] = useState(
    binding?.provider_slug ?? providers[0]?.slug ?? "",
  );
  const provider = providers.find((p) => p.slug === providerSlug);
  // V0060 - feature_path picker. Sources from V0060's
  // provider_feature_support_tbl (`supported_feature_paths` on the
  // catalog row). Empty for providers that aren't yet wired into the
  // V0060 support table - the dialog disables the dropdown in that case.
  const featurePathOptions = provider?.supported_feature_paths ?? [];

  const [featurePath, setFeaturePath] = useState<string>(
    binding?.feature_path ?? featurePathOptions[0] ?? "",
  );
  const [isEnabled, setIsEnabled] = useState(binding?.is_enabled ?? true);
  const [priority, setPriority] = useState(binding?.priority ?? 100);
  const [config, setConfig] = useState<ConfigValue>(
    (binding?.config as ConfigValue | undefined) ?? {},
  );
  const [credentialId, setCredentialId] = useState<string>(binding?.credential_id ?? "");
  // V0053 / #155 - location-tier card-present default device. Only
  // surfaced when feature_path === "pos.payments.card_present" since
  // the column is meaningless for cash / check / ACH / virtual etc.
  const [defaultDeviceId, setDefaultDeviceId] = useState<string>(binding?.default_device_id ?? "");
  const [error, setError] = useState<string | null>(null);

  // Snap to first supported feature_path when provider changes (create
  // mode only - edit/override keep the saved value even if the catalog
  // has since retired the feature).
  if (
    mode !== "edit" &&
    featurePath !== "" &&
    featurePathOptions.length > 0 &&
    !featurePathOptions.includes(featurePath)
  ) {
    setFeaturePath(featurePathOptions[0]!);
  }

  // Credentials - list the set visible at this scope so the user
  // can pick which one to pin (or leave blank to inherit). Key by the
  // actual request params so org/location scopes (which send {}) share
  // one cache entry instead of fragmenting per scope-id.
  const credParams = credentialParamsForScope(scope);
  const credsQuery = useQuery({
    queryKey: ["provider-credentials-for-binding", JSON.stringify(credParams)],
    queryFn: () => apiClient.listProviderCredentials(credParams),
  });
  const creds: ProviderCredential[] = (credsQuery.data?.credentials ?? []).filter(
    (c) => !c.is_deleted && c.provider === providerSlug,
  );

  // Duplicate-binding detection (create mode only). The V0060 partial-
  // unique index on (scope, feature_path, provider_slug) rejects a
  // second binding for the same triple at the API; surface that as an
  // inline gate before the operator hits Create + sees a raw 409.
  const duplicateExisting: ResolvedBinding | undefined =
    mode === "create" && providerSlug && featurePath
      ? existingOwnScopeBindings.find(
          (r) =>
            r.effective.provider_slug === providerSlug && r.effective.feature_path === featurePath,
        )
      : undefined;

  // Credential-existence gate. A binding with credential_id=NULL
  // ("Auto-resolve from cascade") is meaningless when the subtree has
  // zero credentials for this provider - the cascade has nothing to
  // resolve to. Block save and offer a deep-link to create one.
  // Skipped for journal-entry providers (Cash, Check, …) whose
  // credential_schema declares no fields - those don't need a credential.
  const providerNeedsCredential = hasProperties(provider?.credential_schema);
  const credentialMissingForAutoResolve =
    mode !== "edit" &&
    providerNeedsCredential &&
    credentialId === "" &&
    credsQuery.isSuccess &&
    creds.length === 0;

  // V0053 / #155 - devices synced on THIS binding (Valor's
  // /sync-devices flow populates the table). Only fetched in edit
  // mode + only when the binding supports device selection.
  const isCardPresent = featurePath === "pos.payments.card_present";
  const isVirtualSubtype =
    featurePath === "pos.payments.virtual" || featurePath === "pos.payments.pay_by_text";
  // Devices are location-specific — the pin only makes sense on a
  // location-scope binding. Partner/org rows inherit downward and have
  // no devices of their own to pin.
  const showDeviceDropdown =
    mode === "edit" &&
    binding != null &&
    scope.kind === "location" &&
    (isCardPresent || isVirtualSubtype);
  const devicesQuery = useQuery({
    enabled: showDeviceDropdown,
    // Reuse the same key as DevicesSection so the dialog reads from
    // the same cache entry (and Sync-devices invalidations propagate
    // here without a duplicate fetch).
    queryKey: ["provider-devices", binding?.id],
    queryFn: () => apiClient.listProviderDevices(binding!.id),
  });
  const isVirtualDevice = (d: ProviderDevice) => {
    const t = String(d.metadata?.device_type ?? "").toLowerCase();
    return t.includes("soft") || t.includes("virtual");
  };
  const activeDevices: ProviderDevice[] = (devicesQuery.data?.devices ?? []).filter((d) => {
    if (!d.is_active) return false;
    if (isCardPresent) return !isVirtualDevice(d);
    if (isVirtualSubtype) return isVirtualDevice(d);
    return true;
  });
  // If the binding's saved default points at a device that's been
  // deactivated or deleted upstream, surface it as a non-selectable
  // option + warning rather than letting the controlled <select>
  // silently render an empty value while still PATCHing the stale id.
  const hasStalePinnedDevice =
    defaultDeviceId !== "" &&
    devicesQuery.isSuccess &&
    !activeDevices.some((d) => d.id === defaultDeviceId);

  const scopeBody =
    scope.kind === "partner"
      ? { partner_id: scope.id }
      : scope.kind === "organization"
        ? { organization_id: scope.id }
        : { location_id: scope.id };

  // Push 13: strip keys whose x-scope is more specific than this
  // dialog's scope, AND keys whose x-scope is less specific (inherited
  // and shown read-only). What leaves the form is only the bag of
  // fields this scope actually owns - preventing accidental overrides
  // of ancestor fields when the form was seeded from a merged
  // ResolvedBinding.
  //
  // Fields not present in the schema default to x-scope "location"
  // (matching backend validate_config_scope behaviour). This means a
  // partner/org row won't accidentally carry stale location-tier keys
  // that were written before the schema added the x-scope annotation.
  const configForSubmit = useMemo(() => {
    if (!provider) return config;
    const props = provider.config_schema.properties ?? {};
    const out: ConfigValue = {};
    for (const [k, v] of Object.entries(config)) {
      const field = props[k] ?? {};
      const raw = (field as unknown as Record<string, unknown>)["x-scope"];
      const fs: BindingScope =
        raw === "partner" || raw === "organization" || raw === "location" ? raw : "location";
      if (fs === scope.kind) out[k] = v;
    }
    return out;
  }, [config, provider, scope.kind]);

  const save = useMutation({
    mutationFn: async () => {
      if (mode === "edit" && binding) {
        return apiClient.patchProviderBinding(binding.id, {
          is_enabled: isEnabled,
          priority,
          config: configForSubmit,
          credential_id: credentialId || null,
          ...(showDeviceDropdown ? { default_device_id: defaultDeviceId || null } : {}),
        });
      }
      // Create-or-override: both hit POST (override = new row at the
      // current scope shadowing the ancestor's). V0060 - feature_path
      // is the single routing key.
      return apiClient.createProviderBinding({
        ...scopeBody,
        provider_slug: providerSlug,
        feature_path: featurePath,
        is_enabled: isEnabled,
        priority,
        config: configForSubmit,
        ...(credentialId ? { credential_id: credentialId } : {}),
      });
    },
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <ModalOverlay
      ariaLabel={mode === "edit" ? "Edit binding" : "Add provider"}
      onClose={props.onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="w-full max-w-xl space-y-3 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl"
      >
        <h2 className="text-sm font-semibold">
          {mode === "edit" ? "Edit binding" : "Add provider"} ·{" "}
          <span className="font-normal text-muted-foreground">
            {scope.kind} / {scope.name}
          </span>
        </h2>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Provider</span>
            <select
              value={providerSlug}
              onChange={(e) => {
                setProviderSlug(e.target.value);
                // Snap feature_path to the new provider's first
                // supported feature_path on provider change.
                const next = providers.find((p) => p.slug === e.target.value);
                setFeaturePath(next?.supported_feature_paths[0] ?? "");
              }}
              disabled={mode === "edit"}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm"
            >
              {providers.map((p) => (
                <option key={p.slug} value={p.slug} disabled={!p.is_enabled}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Feature</span>
            <select
              value={featurePath}
              onChange={(e) => setFeaturePath(e.target.value)}
              disabled={mode === "edit" || featurePathOptions.length === 0}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm"
            >
              {featurePathOptions.length === 0 ? (
                <option value="">No features supported</option>
              ) : (
                featurePathOptions.map((path) => (
                  <option key={path} value={path}>
                    {humanizeFeaturePath(path)}
                  </option>
                ))
              )}
            </select>
            {featurePath && (
              <span className="mt-1 block font-mono text-[10px] text-muted-foreground">
                {featurePath}
              </span>
            )}
          </label>
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <div className="flex items-center">
            <Toggle size="sm" checked={isEnabled} onChange={setIsEnabled} label="Enabled" />
          </div>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Priority (lower wins)</span>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs sm:col-span-1">
            <span className="mb-1 block text-muted-foreground">Credential (pin)</span>
            <select
              value={credentialId}
              onChange={(e) => setCredentialId(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm"
            >
              <option value="">
                {describeAutoOption(props.resolved, hasProperties(provider?.credential_schema))}
              </option>
              {creds.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} · {c.environment}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* V0053 / #155 - Default-device pin for card_present, virtual,
            and pay_by_text bindings. Hidden on every other subtype. */}
        {showDeviceDropdown && (
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">
              {isCardPresent ? "Default card-present device" : "Default virtual terminal"}
            </span>
            {devicesQuery.isPending ? (
              <div className="rounded-md border bg-muted/30 px-2 py-1 text-[11px] text-muted-foreground">
                Loading devices…
              </div>
            ) : devicesQuery.isError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                Couldn&apos;t load devices: {describe(devicesQuery.error)}
              </div>
            ) : activeDevices.length === 0 && !hasStalePinnedDevice ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                {isCardPresent
                  ? 'No active devices synced on this binding yet. Run "Sync devices" in the Valor section before pinning a default.'
                  : 'No active Soft POS devices synced on this binding yet. Run "Sync devices" in the Valor section before pinning a default.'}
              </div>
            ) : (
              <>
                <select
                  value={defaultDeviceId}
                  onChange={(e) => setDefaultDeviceId(e.target.value)}
                  className="w-full rounded-md border bg-background px-2 py-1 text-sm"
                >
                  <option value="">No default - cashiers pick at sale time</option>
                  {hasStalePinnedDevice && (
                    <option value={defaultDeviceId}>
                      Currently pinned (inactive or removed) · {defaultDeviceId}
                    </option>
                  )}
                  {activeDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.display_name ? `${d.display_name} · ${d.device_ref}` : d.device_ref}
                    </option>
                  ))}
                </select>
                {hasStalePinnedDevice && (
                  <span className="mt-1 block rounded-md border border-amber-500/40 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    The saved default points at an inactive or removed device. Pick an active device
                    or clear the default before saving.
                  </span>
                )}
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {isCardPresent
                    ? "Cashiers can override this for a single sale or pin their own default in their profile."
                    : "Cashiers can override this at sale time by selecting a different terminal from the dropdown."}
                </span>
              </>
            )}
          </label>
        )}

        {provider && hasProperties(provider.config_schema) ? (
          <SchemaForm
            schema={provider.config_schema}
            values={config}
            onChange={(next) => setConfig(next as ConfigValue)}
            caption={`Fields below come from ${provider.display_name}'s config schema. Required fields are marked with an asterisk. Edits override inherited values at the ${scope.kind} scope.`}
            // Push 13: only render fields owned by the current scope as
            // editable. Fields that live at an ancestor scope appear
            // read-only above with an "inherited from {scope}" pill.
            scopeFilter={scope.kind}
            inheritedValues={buildInheritedValues(props.resolved, scope.kind)}
            // Expose the current feature_path so provider schemas can
            // gate feature-specific fields via visible_when (Valor:
            // card_present_devices only on pos.payments.card_present;
            // virtual_terminal_ids only on .virtual / .pay_by_text).
            ambient={{
              feature_path: featurePath,
            }}
            enumResolver={async (source, values) => {
              if (!source.endpoint) return source.options ?? [];
              // Substitute :credential_id + any depends_on sibling
              // values into the endpoint template. When the row's
              // own credential pin is empty ("Inherit from ancestor")
              // fall back to the ancestor-resolved credential so the
              // dropdowns still fetch; the resolver endpoints are
              // credential-scoped at the provider level and don't
              // care which scope in the ancestry owns the row.
              const effectiveCredId = credentialId || props.resolvedCredentialId || "";
              if (!effectiveCredId && source.endpoint.includes(":credential_id")) {
                return [];
              }
              let path = source.endpoint.replace(
                ":credential_id",
                encodeURIComponent(effectiveCredId),
              );
              for (const dep of source.depends_on ?? []) {
                const v = values[dep];
                if (typeof v === "string") {
                  path = path.replace(`:${dep}`, encodeURIComponent(v));
                }
              }
              const resp = await apiClient.fetchEnumOptions(path);
              return resp.options;
            }}
          />
        ) : (
          <div className="rounded-md border bg-muted/30 p-3">
            <p className="text-[11px] text-muted-foreground">
              {provider
                ? `${provider.display_name} has no configurable fields. Submit as-is.`
                : "Select a provider above to see its config fields."}
            </p>
          </div>
        )}

        {duplicateExisting && (
          <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <p className="font-medium">
              {provider?.display_name ?? providerSlug} is already bound to{" "}
              {humanizeFeaturePath(featurePath)} at this {scope.kind}.
            </p>
            <p className="mt-1">
              Each (provider, feature) pair can only have one binding per scope. Edit the existing
              binding instead, or pick a different provider or feature above.
            </p>
            {props.onSwitchToEdit && (
              <button
                type="button"
                onClick={() => {
                  props.onSwitchToEdit?.(duplicateExisting.effective, duplicateExisting);
                }}
                className="mt-2 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
              >
                Edit existing binding
              </button>
            )}
          </div>
        )}
        {credentialMissingForAutoResolve && !duplicateExisting && (
          <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <p className="font-medium">
              No {provider?.display_name ?? providerSlug} credentials found (at this {scope.kind} or
              any ancestor scope).
            </p>
            <p className="mt-1">
              {provider?.display_name ?? providerSlug} requires a credential to call the vendor.
              "Auto-resolve from cascade" has nothing to resolve - calls will fail at runtime.
              Create a credential first, or pin one explicitly above.
            </p>
            <Link
              to={buildCreateCredentialHref(scope, providerSlug)}
              className="mt-2 inline-block rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
            >
              Create credential
            </Link>
          </div>
        )}
        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={
              save.isPending ||
              !providerSlug ||
              !featurePath ||
              !!duplicateExisting ||
              credentialMissingForAutoResolve
            }
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {mode === "edit" ? "Save" : mode === "override" ? "Override at this scope" : "Create"}
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function credentialParamsForScope(scope: ProvidersTabScope): {
  partner_id?: string;
  organization_id?: string;
  location_id?: string;
} {
  if (scope.kind === "partner") return { partner_id: scope.id };
  // For org and location scopes, a credential at any ancestor scope
  // (org or partner) can auto-resolve. Filtering by exact org_id /
  // location_id would hide those ancestor credentials and produce a
  // false "no credentials" warning. The backend's permission filter
  // already restricts results to the caller's partner scope.
  return {};
}

/**
 * Deep-link to /provider-credentials with the create dialog pre-targeted
 * at the binding's scope + provider so the operator doesn't have to
 * reselect either after following the "Create credential" CTA from the
 * BindingDialog's missing-credential banner.
 *
 * The /provider-credentials page reads these query params on mount.
 */
function buildCreateCredentialHref(scope: ProvidersTabScope, providerSlug: string): string {
  const params = new URLSearchParams();
  params.set("create", "1");
  params.set("provider", providerSlug);
  params.set("scope_kind", scope.kind);
  params.set("scope_id", scope.id);
  return `/provider-credentials?${params.toString()}`;
}

/**
 * Push 13c: dynamic label for the "no pin set" option in the
 * Credential dropdown. Shows the resolver's auto-pick so admins can
 * see *which* credential will actually be used, or the blunt "no
 * credential - payments will fail" state when the resolver can't
 * find one (for providers that actually need a credential).
 *
 * Journal-entry providers (Cash, Check, …) declare an empty
 * credential_schema in the catalog - credential_source being null is
 * normal, not a failure. The ``providerNeedsCredential`` flag
 * suppresses the doom-and-gloom copy in that case.
 *
 * Silent fallback to the pre-push-13c static string when we don't
 * have a ResolvedBinding (create flow) or a provider yet.
 */
function describeAutoOption(
  resolved: ResolvedBinding | undefined,
  providerNeedsCredential: boolean,
): string {
  // "Cascade" instead of "ancestor" because at the partner-root scope
  // there is no real ancestor - Platform doesn't store credentials -
  // and even at lower tiers the resolver picks the most-specific match,
  // which can be the binding's own scope, not strictly an ancestor.
  if (!resolved) return "Auto-resolve from cascade";
  const cs = resolved.credential_source;
  if (cs == null) {
    return providerNeedsCredential
      ? "Auto-resolve - no credential found (payments will fail)"
      : "No credential required for this provider";
  }
  if (cs.auto_resolved) {
    const label = cs.credential_label ?? "(unlabelled credential)";
    const scope = cs.credential_scope_name ?? cs.credential_scope_kind ?? "cascade";
    return `Auto-resolve → ${label} (from ${scope})`;
  }
  // Explicit pin already exists - the dropdown is about to replace
  // it with a new value, so the "no pin" option should describe what
  // happens if you clear the pin.
  return "Auto-resolve from cascade";
}

/**
 * Build the SchemaForm's ``inheritedValues`` bag from a
 * ResolvedBinding. Includes fields whose provenance is either a
 * less-specific scope than the one the dialog is editing OR
 * ``catalog-default`` (push 15c: fell back to config_schema's default
 * because no binding row supplied a value). The SchemaForm renders
 * both as read-only rows with the correct "from partner" / "catalog
 * default" pill.
 */
function buildInheritedValues(
  resolved: ResolvedBinding | undefined,
  currentScope: BindingScope,
): Record<string, { value: unknown; scope: ProvenanceSource }> | undefined {
  if (!resolved) return undefined;
  const prov = resolved.provenance;
  if (!prov) return undefined;
  const rank: Record<BindingScope, number> = { partner: 1, organization: 2, location: 3 };
  const effective = (resolved.effective.config as Record<string, unknown> | null | undefined) ?? {};
  const out: Record<string, { value: unknown; scope: ProvenanceSource }> = {};
  for (const [key, source] of Object.entries(prov)) {
    if (source === "catalog-default") {
      // Catalog-default fields always render as inherited regardless
      // of current scope - no binding row owns them, so the admin's
      // way to change them is to override at the current scope.
      out[key] = { value: effective[key], scope: "catalog-default" };
      continue;
    }
    if (rank[source] < rank[currentScope]) {
      out[key] = { value: effective[key], scope: source };
    }
  }
  return out;
}

// Eslint silence for unused types.
export type _WiringOnly = BindingScope;

/** Format an ltree feature path as a human-readable label.
 *  Uses all segments after the root app prefix (pos/kiosk) joined
 *  by " / " so sibling paths with the same leaf are distinguishable
 *  (e.g. pos.notifications.sms vs kiosk.receipt.sms both end in
 *  "sms" but display as "Notifications / Sms" vs "Receipt / Sms"). */
function humanizeFeaturePath(path: string): string {
  const segments = path.split(".");
  const relevant = segments.length > 1 ? segments.slice(1) : segments;
  return relevant
    .map((seg) => {
      const pretty = seg.replace(/_/g, " ");
      return pretty.charAt(0).toUpperCase() + pretty.slice(1);
    })
    .join(" / ");
}
